import React from 'react';

interface TextInputProps {
  label?: string;
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
  type?: string;
  mask?: 'date';
}

const TextInput: React.FC<TextInputProps> = ({
  label,
  value,
  placeholder,
  onChange,
  type = 'text',
  mask,
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (mask === 'date') {
      const digits = v.replace(/\D/g, '').slice(0, 8);
      let formatted = digits;
      if (digits.length > 4) {
        formatted = digits.slice(0, 2) + '/' + digits.slice(2, 4) + '/' + digits.slice(4);
      } else if (digits.length > 2) {
        formatted = digits.slice(0, 2) + '/' + digits.slice(2);
      }
      onChange(formatted);
    } else {
      onChange(v);
    }
  };

  // Build dynamic placeholder display for date mask
  const renderDateOverlay = () => {
    const template = 'dd/mm/yyyy';
    const rawDigits = value.replace(/\D/g, '').slice(0, 8);
    let dayPart = template.slice(0, 2);
    let monthPart = template.slice(3, 5); // mm
    let yearPart = template.slice(6); // yyyy

    // Fill day digits
    if (rawDigits.length >= 1)
      dayPart = rawDigits[0] + (rawDigits.length >= 2 ? rawDigits[1] : 'd');
    // Fill month digits
    if (rawDigits.length >= 3)
      monthPart = rawDigits[2] + (rawDigits.length >= 4 ? rawDigits[3] : 'm');
    // Fill year digits
    if (rawDigits.length >= 5) {
      const yearDigits = rawDigits.slice(4); // up to 4
      yearPart = yearDigits + 'yyyy'.slice(yearDigits.length);
    }

    // Construct formatted typed string (with slashes where appropriate)
    let typedFormatted = '';
    if (rawDigits.length <= 2) {
      typedFormatted = rawDigits; // no slash yet until we auto-insert onChange
    } else if (rawDigits.length <= 4) {
      typedFormatted = rawDigits.slice(0, 2) + '/' + rawDigits.slice(2);
    } else {
      typedFormatted =
        rawDigits.slice(0, 2) + '/' + rawDigits.slice(2, 4) + '/' + rawDigits.slice(4);
    }

    // Split overlay into typed + remainder based on typedFormatted length inside the template display
    // Build full display from parts with slashes
    const fullDisplay = dayPart + '/' + monthPart + '/' + yearPart;
    const typedLen = typedFormatted.length;
    const typedSegment = fullDisplay.slice(0, typedLen);
    const remainderSegment = fullDisplay.slice(typedLen);

    return (
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          padding: '10px 12px',
          width: '100%',
          pointerEvents: 'none',
          fontSize: 14,
          fontFamily: 'inherit',
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
        }}
      >
        <span style={{ color: '#1A1D21' }}>{typedSegment}</span>
        <span style={{ color: '#9AA2AC' }}>{remainderSegment}</span>
      </div>
    );
  };

  return (
    <div
      style={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        position: mask === 'date' ? 'relative' : undefined,
      }}
    >
      {label && <label style={{ fontSize: 12 }}>{label}</label>}
      <div style={{ position: 'relative' }}>
        {mask === 'date' && renderDateOverlay()}
        <input
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={handleChange}
          inputMode={mask === 'date' ? 'numeric' : undefined}
          maxLength={mask === 'date' ? 10 : undefined}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 12,
            border: '1px solid #D9DDE2',
            background: 'transparent',
            fontSize: 14,
            letterSpacing: mask === 'date' ? 0.5 : undefined,
            position: 'relative',
            color: mask === 'date' ? 'transparent' : '#1A1D21',
            caretColor: '#1A1D21',
          }}
        />
      </div>
    </div>
  );
};

export default TextInput;
